<template>
  <div class="fitness-page">
    <div class="fitness-page__top">
      <AlertBar
        v-if="currentPage !== 'mental-side'"
        icon-name="icon_info"
        class="fitness-page__alert"
      >
        <p
          class="fitness-page__alert-text"
          v-html="$t('fitness_get_upgrade_message')"
        />
      </AlertBar>
      <ProgressBar
        class="fitness-page__progress"
        :progress="pageProgress"
      />
    </div>
    <div class="fitness-page__body">
      <slot />
    </div>
    <div class="fitness-page__disclaimer">
      <p v-html="$t(disclaimerText, [disclaimerSettings.price, disclaimerSettings.subscription_period])" />
    </div>
    <teleport to="body">
      <LazyCheckoutModal
        :form="$versions.paymentForm === '3' ? 'PaddleCheckoutForm' : 'BasicCheckoutForm'"
        :payment-data="$attrs.paymentData as PaymentModel"
        :payment-params="$attrs.paymentParams as BasePaymentModel"
        :show-description="false"
        :stage="$attrs.stage as string"
        :upsale="true"
        @action="$attrs.action as void"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import AlertBar from '@/components/shared/AlertBar.vue';
import ProgressBar from '@/components/upsale/ProgressBar.vue';
import LazyCheckoutModal from '~/components/modal/LazyCheckoutModal.vue';
import type { BasePaymentModel, PaymentModel } from '~/common/constants/SubscriptionPlans.ts';

export interface UpsalePaymentProps {
  paymentData: PaymentModel
  paymentParams: BasePaymentModel
  stage: string
  action: () => void
}

const route = useRoute();
const { t } = useI18n();
const { $db, $versions } = useNuxtApp();
const paymentConfig = usePaymentConfigs();

const currentPage = computed((): string => route.path.split('/')[route.path.split('/').length - 1]);
const localSubscriptionInfo = await $db?.getItem('subscriptionInfo');
const subscription = import.meta.client && localSubscriptionInfo;

const disclaimerSettings = computed((): { price: string, subscription_period: string } => {
  const isDiscount = currentPage.value === 'super-discount';
  const idx = subscription?.base?.id - 1;
  const currencyCode = paymentConfig.currentConfig[idx]?.base?.currencyCode;
  const plansKeys = ['subscription_period_1week', 'subscription_period_1month', 'subscription_period_3months'];
  let prices;
  if (import.meta.client && subscription?.addons) {
    prices = isDiscount ? subscription?.addons.fitness.super : subscription?.addons.fitness.standard;
  }

  return {
    price: `${prices?.discPrice?.toFixed(2)} ${currencyCode}`,
    subscription_period: t(`${plansKeys[idx]}`) || '',
  };
});

const pageProgress = computed((): number => {
  switch (currentPage.value) {
    case 'did-you-know':
      return 63;
    case 'super-discount':
      return 70;
    case 'sexology':
      return 75;
    case 'mental-side':
      return 80;
    case 'super-price':
      return 87;
    case 'basic-discount':
    default:
      return 50;
  }
});

const disclaimerText = computed((): string => {
  if (
    currentPage.value === 'sexology'
    || currentPage.value === 'mental-side'
    || currentPage.value === 'super-price'
  ) {
    return 'oldTranslations.upsale.courses.disclaimer';
  }

  if (subscription?.isEmailLanding) {
    return 'oldTranslations.upsale.fitness.emailLandDisclaimer';
  }

  if (currentPage.value === 'super-discount') {
    return 'fitness_super_disc_subscription_terms';
  }

  return subscription?.base?.id === 1 ? 'fitness_subscription_terms_1week' : 'fitness_subscription_terms_1and3_months';
});
</script>

<style lang="scss" scoped>
.fitness-page {
  max-width: 480px;
  margin: 0 auto;
  padding-bottom: 100px;
  width: 100%;

  &__top {
    padding: 0 32px;
  }
  &__alert {
    margin-bottom: 16px;
  }
  &__alert-text {
    color: $text-color;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  &__progress {
    margin-bottom: 32px;
  }

  &__body {
    margin-bottom: 32px;
  }

  &__disclaimer {
    padding: 0 32px;

    & p {
      color: $disclaimer-text;
      text-align: center;
      font-size: 10px;
      font-style: normal;
      font-weight: 300;
      line-height: 150%; /* 15px */
    }

    &--bold {
      font-weight: 600;
    }
  }
}
</style>
